/**
 * dataTableDirective
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { capitalize, slugify } from '@shared/utils/strings';

export function dataTableDirective() {
    return {
        transclude: true,
        //language=HTML
        template: `
            <div class="data-table-container">
                <table class="data-table">
                    <thead>
                    <tr>
                        <th ng-if="selectable"
                            class="app-data-table-checkbox-cell"
                            ng-click="_swallowEvent($event)">
                            <label class="app-data-table-checkbox">
                                <input type="checkbox">
                            </label>
                        </th>

                        <th ng-repeat="col in ::columns"
                            class="{{ ::cellClass(col) }}"
                            ng-style="::{
                                'min-width': (widths[$index] || 0),
                                width: (widths[$index] || 'auto'),
                            }">
                            {{ heading(col) }}
                        </th>

                        <th ng-if="actions.length > 0"></th>
                    </tr>
                    </thead>

                    <tbody ng-if="!loading">
                    <tr ng-repeat="item in items track by trackBy ? item[trackBy] : $id(item)"
                        ng-click="onItemClicked($event, item)"
                        data-testid="row:{{ trackBy ? item[trackBy] : item.id || $index }}"
                        class="{{ rowClass(item) }}">

                        <td ng-if="selectable"
                            class="app-data-table-checkbox-cell"
                            ng-click="_swallowEvent($event)">
                            <label class="app-data-table-checkbox">
                                <input type="checkbox" ng-click="_swallowEvent($event)">
                            </label>
                        </td>

                        <td ng-repeat="col in ::columns"
                            class="{{ cellClass(col) }}"
                            compile="format(item, col)"
                            data-testid="cell:{{ col }}">
                        </td>

                        <td ng-if="actions.length > 0">
                            <span ng-repeat="action in actions">
                                <span ng-click="onActionClicked($event, item, action.key)">
                                    <md-icon>{{ action.icon }}</md-icon>
                                    <md-tooltip>{{ action.tooltip }}</md-tooltip>
                                </span>
                            </span>
                        </td>
                    </tr>

                    <tr class="data-table-empty" ng-if="!loading && !items.length">
                        <td colspan="100%">
                            {{ emptyMessage || 'Nothing to show' }}
                        </td>
                    </tr>
                    </tbody>
                </table>

                <div class="data-table-loading-container" ng-if="loading">
                    <app-loading small></app-loading>
                </div>
            </div>
        `,
        scope: {
            classNames: '=?classNames',
            columns: '=columns',
            config: '=config',
            emptyMessage: '=emptyMessage',
            formatters: '=formatters',
            headings: '=headings',
            items: '=items',
            loading: '=loading',
            onItemClick: '&onItemClick',
            rowClass: '=?rowClass',
            selectable: '=?selectable',
            trackBy: '@trackBy',
            widths: '=widths',
            actions: '=?actions',
            onActionClick: '&onActionClick',
        },
        link: function (scope) {
            scope.cellClass = cellClass;
            scope.format = format;
            scope.heading = heading;
            scope.itemPath = itemPath;
            scope.onItemClicked = onItemClicked;
            scope.onActionClicked = onActionClicked;

            scope._swallowEvent = $event => {
                console.log('_swallowEvent', $event.target);

                $event && $event.stopPropagation();
                return false;
            };

            scope.classNames = angular.isObject(scope.classNames) ? scope.classNames : {};

            function cellClass(col) {
                if (scope.classNames.hasOwnProperty(col)) {
                    return `cell--${scope.classNames[col]}`;
                }

                return `cell--${slugify(col)}`;
            }

            function format(item, column) {
                const value = itemPath(item, column);

                if (scope.formatters) {
                    if (cellCache.contains(column, value)) {
                        return cellCache.get(column, value);
                    }

                    if (!angular.isObject(scope.formatters)) {
                        throw '`formatters` attribute must be an object';
                    }

                    if (scope.formatters.hasOwnProperty(column)) {
                        if (!angular.isFunction(scope.formatters[column])) {
                            throw 'Formatter must be a function';
                        }

                        const formatted = scope.formatters[column].apply(null, [value, item]);

                        cellCache.put(column, value, formatted);

                        return formatted;
                    }
                }

                if (value === true) {
                    return `<md-icon>check</md-icon>`;
                }

                if (value === false) {
                    return `<md-icon>clear</md-icon>`;
                }

                return value;
            }

            function heading(col: string) {
                if (angular.isObject(scope.headings) && scope.headings.hasOwnProperty(col)) {
                    return scope.headings[col];
                }

                return capitalize(
                    col.split('.').pop(),
                );
            }

            /**
             * Returns the element at the given dot-notated path. Code to get element at path
             * taken from: http://stackoverflow.com/a/10934946/1438308
             *
             * @param  {Object} item The object we're searching.
             * @param  {string} path The dot-notated path we're looking for.
             * @return {*}
             */
            function itemPath(item, path) {
                if (path.substr(0, 2) === '$$') {
                    return item;
                }

                return path
                    .split('$$')[0]
                    .split('.')
                    .reduce(
                        (previousValue, currentValue) => (previousValue ? previousValue[currentValue] : undefined),
                        item,
                    );
            }

            function onItemClicked($event, item) {
                if (angular.isFunction(scope.onItemClick)) {
                    scope.onItemClick({
                        $event,
                        item,
                    });
                }
            }

            function onActionClicked($event, item, action) {
                if (angular.isFunction(scope.onActionClick)) {
                    scope.onActionClick({
                        $event,
                        item,
                        action
                    });
                }
            }

            const cellCache = new class CellCache {
                _cache: any;

                constructor() {
                    this._cache = {
                        _: {
                            originalValues: [],
                            formattedValues: [],
                        },
                    };
                }

                contains(column, originalValue) {
                    return (
                        this._cache.hasOwnProperty(column) &&
                        this._cache[column].originalValues.indexOf(originalValue) > -1
                    );
                }

                get(column, originalValue) {
                    if (this._cache.hasOwnProperty(column)) {
                        const index = this._cache[column].originalValues.indexOf(originalValue);

                        if (index === -1) {
                            // Not found
                            return undefined;
                        }

                        return this._cache[column].formattedValues[index];
                    }
                }

                put(column, originalValue, formattedValue) {
                    if (!this._cache.hasOwnProperty(column)) {
                        this._cache[column] = {
                            originalValues: [],
                            formattedValues: [],
                        };
                    }

                    const index = this._cache[column].originalValues.indexOf(originalValue);

                    if (index === -1) {
                        // Insert into cache
                        this._cache[column].originalValues.push(originalValue);
                        this._cache[column].formattedValues.push(formattedValue);
                    } else {
                        // Replace
                        this._cache[column].originalValues[index] = originalValue;
                        this._cache[column].formattedValues[index] = formattedValue;
                    }
                }

                remove(column) {
                    delete this._cache[column];
                }

                clear() {
                    this._cache = {};
                }
            }();
        },
    };
}
